import type { Slide } from '@/framework/types';
import {
  TitleSlide,
  SectionDivider,
  FigureSlide,
  QuoteSlide,
  ColumnsSlide,
  SingleColumnSlide,
  OutroSlide,
} from '@/framework/layouts';

import problemImg from './images/problem.jpg';
import lakestreamStorageImg from './images/lakestream-storage.png';
import catalogArchitectureImg from './images/catalog-architecture.png';
import streamMetadataImg from './images/stream-metadata.png';
import interoperabilityImg from './images/interoperability.png';
import streamnativeImg from './images/streamnative.jpg';
import ctaBgImg from './images/cta-bg.jpg';

/* ════════════════════════════════════════════════════════════════════
   Slide renderers
   ════════════════════════════════════════════════════════════════════ */

const Slide1 = () => (
  <TitleSlide
    eyebrow="Budapest AI/ML"
    title={<>From Topics <em>to Tables</em></>}
    sub={<>Cataloging streaming data in the Iceberg ecosystem · David Kjerrumgaard, Solution Engineer · StreamNative</>}
  />
);

const Slide2 = () => (
  <ColumnsSlide
    eyebrow="Agenda"
    title={<>Two parts. <em>Pattern, then real world.</em></>}
    variant="cards"
    cols={2}
    columns={[
      {
        num: '01',
        name: 'The Problem & The Pattern',
        desc:
          'The Fragmentation Problem · The Streaming Data Catalog Pattern · Core Metadata Model · Interoperability & Protocol Gateways',
      },
      {
        num: '02',
        name: 'Real World at Scale',
        desc:
          'Automotive Data at Scale · The Pain Before · Implementing the Pattern · Results & Lessons Learned',
      },
    ]}
  />
);

const Slide3 = () => (
  <SectionDivider
    eyebrow="Part 01"
    title={<>The Problem &amp; <em>The Pattern</em></>}
    watermark="01"
  />
);

const Slide4 = () => (
  <FigureSlide
    eyebrow="The Problem"
    title={<>Three systems. <em>No contract between them.</em></>}
    image={problemImg}
    alt="Fragmentation across topics, schemas, and tables"
    framed
  />
);

const Slide5 = () => (
  <ColumnsSlide
    eyebrow="The Hidden Cost of Fragmentation"
    title={<>Four symptoms. <em>You will recognize them.</em></>}
    variant="cards"
    cols={4}
    columns={[
      {
        num: '01',
        name: 'Schema Drift',
        desc: 'Registry evolves, Iceberg sink doesn’t — silent data corruption downstream.',
      },
      {
        num: '02',
        name: 'Governance Gaps',
        desc: 'Lineage traces a table but can’t follow back to the originating topic.',
      },
      {
        num: '03',
        name: 'Onboarding Friction',
        desc: 'New consumers navigate 3+ systems before writing a single query.',
      },
      {
        num: '04',
        name: 'Stateful Connectors',
        desc: 'Every worker holds config that should live somewhere governed — not in memory.',
      },
    ]}
  />
);

const Slide6 = () => (
  <QuoteSlide
    eyebrow="The Pattern"
    quote={<>A Stream is a Table. <em>Not a mirror.</em></>}
    author="The pattern"
    role="One physical copy — two interfaces, one catalog"
  />
);

const Slide7 = () => (
  <FigureSlide
    eyebrow="Pattern · Lakestream Storage Format"
    title={<>One dataset. <em>Two APIs. Zero ETL.</em></>}
    sub="WAL for stream consumers · compacted Parquet for SQL · same bytes, atomically maintained by the catalog."
    image={lakestreamStorageImg}
    alt="Lakestream storage format — WAL plus compacted Parquet, range-based offset index, Iceberg snapshot linkage"
    framed
  />
);

const Slide8 = () => (
  <ColumnsSlide
    eyebrow="Pattern · A Write Request, End to End"
    title={<>Five steps. <em>One write path.</em></>}
    sub="Steps 1–3: streaming latency (milliseconds) · Steps 4–5: analytics latency (seconds to minutes) · StreamCatalog keeps both synchronized."
    variant="cards"
    cols={5}
    columns={[
      {
        num: '01',
        name: 'Producer sends event',
        desc: 'Standard Kafka wire protocol to a UFK broker — zero client-side changes required.',
      },
      {
        num: '02',
        name: 'Protocol server writes to Ursa WAL',
        desc: 'UFK translates the write; event lands directly on object storage (S3, GCS, or ADLS). No local disk.',
      },
      {
        num: '03',
        name: 'WAL acknowledges — broker ACKs producer',
        desc: 'Streaming durability is confirmed. The event is immediately readable at its stream offset.',
      },
      {
        num: '04',
        name: 'Background compaction → Parquet',
        desc: 'Ursa batches events into columnar Parquet files according to the stream’s retention and compaction config.',
      },
      {
        num: '05',
        name: 'Parquet registered as Iceberg snapshot',
        desc: 'StreamCatalog updates the linked lakehouse table. Databricks, Snowflake, and Spark see the new snapshot immediately.',
      },
    ]}
  />
);

const Slide9 = () => (
  <FigureSlide
    eyebrow="Pattern · Catalog Architecture"
    title={<>One metadata layer. <em>Bridges schema registry and Iceberg catalog.</em></>}
    sub="Stateless protocol layer · pluggable Stream Catalog · WAL + lakehouse storage atomically linked."
    image={catalogArchitectureImg}
    alt="Stream Catalog bridging the protocol layer to stream storage and lakehouse storage"
    framed
  />
);

const Slide10 = () => (
  <FigureSlide
    eyebrow="Pattern · Stream-Table Linkage"
    title={<>One dataset. <em>Two faces.</em></>}
    sub="StreamCatalog → Namespace → Stream, with a 1:1 explicit linkage to a Lakehouse Table. Fan-out happens at the query layer."
    image={streamnativeImg}
    alt="StreamCatalog hierarchy with a one-to-one link between Stream and Lakehouse Table"
    framed
  />
);

const Slide11 = () => (
  <FigureSlide
    eyebrow="Pattern · Stream Metadata"
    title={<>Stream metadata the <em>StreamCatalog</em> manages.</>}
    sub="Protocol gateways become stateless — they look up schema, partitioning, retention, and routing from the catalog at startup."
    image={streamMetadataImg}
    alt="Stream metadata managed by the StreamCatalog — schemas, partitions, retention, offsets"
    framed
  />
);

const Slide12 = () => (
  <FigureSlide
    eyebrow="Pattern · Interoperability"
    title={<>Federate. <em>Don’t replace.</em></>}
    sub="The pattern works alongside your existing schema registry and Iceberg catalog — no rip-and-replace."
    image={interoperabilityImg}
    alt="Federation diagram: Stream Catalog integrating with existing schema registry and Iceberg catalog"
    framed
  />
);

const Slide13 = () => (
  <SectionDivider
    eyebrow="Part 02"
    title={<>Real World <em>at Scale</em></>}
    sub="Automotive Manufacturer · Connected Vehicle Telemetry"
    watermark="02"
  />
);

const Slide14 = () => (
  <ColumnsSlide
    eyebrow="Real World · The Data Reality of a Modern Vehicle Fleet"
    title={<>What makes this <em>uniquely hard.</em></>}
    variant="cards"
    cols={3}
    columns={[
      {
        num: '01',
        name: 'Fleet Scale',
        desc:
          'Millions of connected vehicles — each a real-time telemetry stream generating continuous data across the fleet.',
      },
      {
        num: '02',
        name: 'High-Frequency Events',
        desc:
          'CAN bus, GPS, battery management, and ADAS sensors — thousands of events per vehicle per second at peak load.',
      },
      {
        num: '03',
        name: 'Multiple Source Types',
        desc:
          'Telemetry streams · OTA update events · Manufacturing quality signals — all requiring unified, governed access.',
      },
      {
        num: '04',
        name: 'Safety-Critical',
        desc:
          'A missed recall signal = real-world consequence. Schema changes require governance — not Jira tickets.',
      },
      {
        num: '05',
        name: 'Regulatory',
        desc:
          'GDPR + ISO 26262 + UNECE WP.29. Audit trails must trace every event from origin to insight.',
      },
      {
        num: '06',
        name: 'Multi-Protocol Reality',
        desc:
          'Test benches don’t speak Kafka. Cloud backends do. Both must feed the same governed data lake.',
      },
    ]}
  />
);

const Slide15 = () => (
  <ColumnsSlide
    eyebrow="Real World · How We Envision Mapping Our Architecture"
    title={<>Four moves. <em>Aspirational — but concrete.</em></>}
    variant="cards"
    cols={4}
    columns={[
      {
        num: '01',
        name: 'Every Topic → One Stream Entry',
        desc:
          'Every Kafka topic maps to a Stream catalog object, each linked to its Iceberg table. No exceptions — the constraint is what makes the pattern enforceable at fleet scale.',
      },
      {
        num: '02',
        name: 'Registry Kept, Catalog as Wrapper',
        desc:
          'We’d keep our existing schema registry. Teams already trust it. The catalog federates schema IDs — meeting teams where they are, not forcing a rip-and-replace.',
      },
      {
        num: '03',
        name: 'Consumer State Into the Catalog',
        desc:
          'Consumer groups currently tracked in a static database would become catalog queries. Any team could ask “who is consuming vehicle-telemetry at what offset?” without a ticket.',
      },
      {
        num: '04',
        name: 'Retention Policies Centralized',
        desc:
          'Topic retention configs today are scattered across broker configs. In this model they’d be declared once in the catalog, enforced uniformly across all ingestion paths.',
      },
    ]}
  />
);

const Slide16 = () => (
  <SingleColumnSlide
    eyebrow="Real World · Same Data. Three Consumers. Three Different Systems."
    title={<>Today: ETL between every pair. <em>And ETL breaks.</em></>}
    body={
      <>
        <div className="sn-col-grid sn-col-grid--3" style={{ marginBottom: '2.2rem' }}>
          <article className="sn-col sn-col--card">
            <span className="sn-col-num">01</span>
            <h3 className="sn-col-name">Analytics &amp; BI</h3>
            <p className="sn-col-desc">
              <em>Needs:</em> SQL table view.<br />
              Snowflake · Databricks · Spark SQL.
            </p>
          </article>
          <article className="sn-col sn-col--card">
            <span className="sn-col-num">02</span>
            <h3 className="sn-col-name">Stream Processing</h3>
            <p className="sn-col-desc">
              <em>Needs:</em> event log access.<br />
              Flink · Kafka Streams · consumer groups.
            </p>
          </article>
          <article className="sn-col sn-col--card">
            <span className="sn-col-num">03</span>
            <h3 className="sn-col-name">Event-Driven Apps</h3>
            <p className="sn-col-desc">
              <em>Needs:</em> reliable event stream.<br />
              Microservices · alerting · CDC pipelines.
            </p>
          </article>
        </div>
        <p style={{ margin: '0 0 1rem', fontFamily: 'var(--sn-font-mono)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.7 }}>
          Today’s pipeline (ETL breaks)
        </p>
        <p style={{ margin: 0, fontSize: '1.05rem' }}>
          Kafka Topic &nbsp;→&nbsp; Connector &nbsp;→&nbsp; Object Store &nbsp;→&nbsp; Schema Registry Sync &nbsp;→&nbsp; Iceberg Sink &nbsp;→&nbsp; SQL Table
        </p>
        <p style={{ marginTop: '1.6rem', fontSize: '1.15rem' }}>
          <strong>A broken ETL = a missed recall signal.</strong>{' '}
          A firmware update adds a sensor field. The registry updates. The connector doesn’t. Rows drop silently. In automotive, that’s not a dashboard problem — it’s a safety problem.
        </p>
        <p style={{ marginTop: '0.8rem', fontSize: '1.15rem' }}>
          <strong>Stale analytics in a safety-critical domain.</strong>{' '}
          The analytics table is hours behind the event log. When a fleet-wide anomaly emerges, your BI team is looking at yesterday’s data. Governance spanning four systems means no single team owns the gap.
        </p>
      </>
    }
  />
);

const Slide17 = () => (
  <FigureSlide
    eyebrow="Call for Conversation"
    title={<>Four questions. <em>Spec is yours.</em></>}
    sub="Catalog interface — does it feel right? · Stream metadata next to table metadata in the Iceberg spec? · How to spec fan-out / fan-in? · First-class in Iceberg, or a separate extension? · Find us after the session."
    image={ctaBgImg}
    alt="Call to action — open conversation with the Iceberg community"
    framed
  />
);

const Slide18 = () => (
  <OutroSlide
    eyebrow="Thank you"
    title={<>Questions? <em>Find us after the session.</em></>}
    email="david.kjerrumgaard@streamnative.io"
    url="streamnative.io"
  />
);

const Slide19 = () => (
  <SingleColumnSlide
    eyebrow="Demo 01 · Ursa: Stream = Table"
    title={<>Same bytes. <em>Now a table.</em></>}
    body={
      <>
        <p style={{ fontFamily: 'var(--sn-font-mono)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.7, margin: '0 0 0.6rem' }}>
          Ursa Engine — VLDB 2025 Best Industry Paper
        </p>
        <ol style={{ margin: '0 0 1.6rem', paddingLeft: '1.4rem', fontSize: '1.1rem', lineHeight: 1.5 }}>
          <li>Kafka events → Ursa WAL → object storage</li>
          <li>DuckDB query: same bytes, now a table</li>
          <li>No connector, no ETL — one physical copy</li>
        </ol>
        <pre style={{
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 12,
          padding: '1.2rem 1.4rem',
          fontFamily: 'var(--sn-font-mono)',
          fontSize: '0.95rem',
          lineHeight: 1.45,
          overflow: 'auto',
          margin: 0,
        }}>{`# Produce temperature events (Bavaria sensors)
python3 scripts/kafka-produce.sh

# Same events — queryable as Iceberg immediately
SELECT sensor_id, temperature_c, location, event_time
FROM   iot.temperature_readings
ORDER BY event_time DESC
LIMIT 10;

# Count: Ursa WAL → Parquet → Iceberg
SELECT count(*) FROM iot.temperature_readings;`}</pre>
      </>
    }
  />
);

const Slide20 = () => (
  <SingleColumnSlide
    eyebrow="Demo 02 · Protocol Expansion"
    title={<>One table. <em>Two protocols.</em></>}
    body={
      <>
        <p style={{ fontFamily: 'var(--sn-font-mono)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.7, margin: '0 0 0.6rem' }}>
          Lakehouse Sink Connector — open-source · Iceberg · Delta Lake · Hudi
        </p>
        <ol style={{ margin: '0 0 1.6rem', paddingLeft: '1.4rem', fontSize: '1.1rem', lineHeight: 1.5 }}>
          <li>Kafka producer: Bavaria sensors 001–010</li>
          <li>Pulsar producer: Rhineland sensors 101–110</li>
          <li>DuckDB: both sensor fleets in one Iceberg table</li>
        </ol>
        <pre style={{
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 12,
          padding: '1.2rem 1.4rem',
          fontFamily: 'var(--sn-font-mono)',
          fontSize: '0.95rem',
          lineHeight: 1.45,
          overflow: 'auto',
          margin: 0,
        }}>{`# Terminal 1: Kafka producer (Bavaria)
bash scripts/kafka-produce.sh

# Terminal 2: Pulsar producer (Rhineland)
bash scripts/pulsar-produce.sh

# Terminal 3: DuckDB — one table, two protocols
SELECT sensor_id, temperature_c, location
FROM   iot.temperature_readings
ORDER BY event_time DESC;

-- sensor-001 Munich   (Kafka)
-- sensor-105 Cologne  (Pulsar)`}</pre>
      </>
    }
  />
);

/* ════════════════════════════════════════════════════════════════════
   Slide order — VERBATIM speaker notes from the source pptx.
   Do not paraphrase. Do not "improve". Talk in 20 minutes.
   ════════════════════════════════════════════════════════════════════ */

export const slides: readonly Slide[] = [
  {
    id: 1, theme: 'beige',
    section: 'Budapest AI/ML · From Topics to Tables',
    notes: [
      "You've already solved the table problem — you're running Iceberg.",
      "So why does getting data from a Kafka topic into that Iceberg table still require three systems, a connector farm, and a prayer that nothing drifts?",
      "What if your protocol gateway held zero state? What if it looked up everything — schema, partition strategy, retention, routing — from a catalog at startup?",
      "Today we're going to make the case that streaming data deserves first-class catalog citizenship.",
    ],
    render: Slide1,
  },
  {
    id: 2, theme: 'beige',
    section: 'Agenda',
    notes: [
      "Quick preview — we'll move fast. Two parts: the pattern, then the real-world story.",
      "Part one — the fragmentation problem, the catalog design, and an architectural overview.",
      "Part two — what this pattern means when your data is coming from millions of connected vehicles.",
      "Let's go.",
    ],
    render: Slide2,
  },
  {
    id: 3, theme: 'beige',
    section: 'Part 01 · The Problem & The Pattern',
    notes: [
      "Pause here for one breath.",
      "'Let's start with the problem — the one you probably recognize if you've ever tried to get Kafka data into an Iceberg table reliably.'",
    ],
    render: Slide3,
  },
  {
    id: 4, theme: 'beige',
    section: 'The Problem',
    notes: [
      "Three systems, no contract between them.",
      "Topics in Kafka. Schemas in the registry. Tables in the catalog. Stitched together by connectors that run on convention, not contract.",
      "I want to be precise about the storage-layer critique here, because it's worth defending carefully. Flink's Table API already treats streams as tables conceptually — that's not the problem. The problem is physical: every existing approach stores the stream in one system and the table in another, then synchronizes them. That synchronization is the failure point.",
      "If you've ever run Kafka Connect plus Schema Registry plus an Iceberg sink — you've got one foot in the stream world and one foot in the table world, and the connectors are doing the splits.",
    ],
    render: Slide4,
  },
  {
    id: 5, theme: 'beige',
    section: 'The Hidden Cost of Fragmentation',
    notes: [
      "Let me walk through the four symptoms. You'll recognize them.",
      "Schema drift. Your registry evolves. Your Iceberg sink doesn't. Silent data corruption downstream. Nobody notices until a dashboard breaks — or worse, until a business decision gets made on quietly wrong data. The insidious thing about schema drift is that there's no error. No alert. The pipeline keeps running. The data just stops meaning what you think it means.",
      "Governance gaps. You can trace lineage to a table, but you can't follow it back to the originating topic. Half your data story is invisible. An auditor asks \"where did this come from?\" and the honest answer is \"we know it arrived in this table, but we can't tell you the full path.\" In a regulated industry that's not just embarrassing — it's a compliance problem.",
      "Onboarding friction. A new engineer joins the team and needs to consume a stream. They start in Kafka, get sent to the schema registry, then get pointed at the Iceberg catalog, then discover the connector config lives somewhere else entirely. Three or more systems before they can write a single query. We call this a developer experience problem. It's actually a governance problem. Every handoff between systems is a place where definitions diverge, versions drift, and ownership becomes unclear.",
      "Stateful connectors. Configuration that should live somewhere governed is instead living in connector worker memory. Partition mappings, schema versions, offset checkpoints — all of it locked inside a process that nobody's watching. When that worker restarts at 2am, that config is gone. And the on-call engineer at 2am is now doing archaeology, not operations.",
      "These aren't edge cases. They're the default state of every streaming-to-lakehouse pipeline running in production today.",
      "Sound familiar?",
    ],
    render: Slide5,
  },
  {
    id: 6, theme: 'beige',
    section: 'The Pattern · One Dataset, Two Faces',
    notes: [
      "This is the pattern we've been building toward.",
      "Not a mirror. Not a copy. Not a view. One physical copy of data — accessible as an append-only event log and as a SQL-queryable lakehouse table.",
      "The StreamCatalog follows the Apache Iceberg catalog interface — the same interface this community already owns. That's not an accident. That's why we're here specifically.",
      "And that's exactly why we're presenting this today — we want your feedback. Does this belong in the Iceberg spec? How should stream-table linkage sit alongside table metadata? We'll dig into those questions at the end, but keep them in mind as we walk through the rest of the pattern.",
    ],
    render: Slide6,
  },
  {
    id: 7, theme: 'beige',
    section: 'Pattern · Lakestream Storage Format',
    notes: [
      "This diagram is the core of how stream-table duality actually works at the storage layer. Two APIs, one dataset — no copy, no sync job, no connector.",
      "On the left, the Lakehouse Table API sees Table Metadata backed by Compacted Parquet files. That's your SQL-queryable, Iceberg-compatible table. On the right, the Stream API sees the Stream Offset Index, which maps logical offsets to physical WAL objects — the low-latency, append-only side.",
      "The dotted line in the middle is the Compaction Point. WAL segments are periodically converted to Parquet in the background. The key word is atomically — when that conversion happens, the offset index and the Iceberg snapshot update in a single transaction. You never see half-compacted state. Consumers always see either the old WAL segments or the new Parquet file, never both or neither.",
      "The offset index design is worth highlighting: it's range-based. Rather than storing one entry per message, a single entry covers a continuous range — start to end — pointing to one WAL object or compacted file. A million-message segment is one index entry. That's what lets the system handle billions of messages across millions of partitions without metadata overhead becoming the bottleneck.",
      "Bottom line: stream consumers read from WAL, SQL engines query Parquet, and the catalog holds both in sync. Same bytes. Different interfaces. Zero ETL.",
    ],
    render: Slide7,
  },
  {
    id: 8, theme: 'beige',
    section: 'Pattern · A Write Request, End to End',
    notes: [
      "This slide is worth slowing down on because it's where the architecture stops being abstract. Let me walk a single event from producer to Iceberg snapshot — and show you where the latency lives.",
      "Step 1 — Producer sends event. Standard Kafka wire protocol to a UFK broker. Zero client-side changes required. If you're running a Kafka producer today, you point it at Ursa and it works. No new SDK, no migration, no rewrite. That matters because the last thing you want when you're simplifying your architecture is to create a new adoption cliff.",
      "Step 2 — Protocol server writes to Ursa WAL. This is where it diverges from traditional Kafka. UFK translates the write and the event lands directly on object storage — S3, GCS, or ADLS. No local disk. No broker-attached EBS volume that becomes a bottleneck at scale, an availability risk during failures, and a cost center every month. The WAL is the source of truth from the moment the write lands.",
      "Step 3 — WAL acknowledges, broker ACKs the producer. Streaming durability is confirmed. The event is immediately readable at its stream offset. This is the end of the streaming latency domain — we're talking milliseconds. A Kafka consumer, a Flink job, a microservice — anything reading the stream sees this event right now.",
      "Step 4 — Background compaction groups events into Parquet. This happens asynchronously. Ursa batches events into columnar Parquet files according to the stream's retention and compaction config. No writes are blocked. No reads are blocked. The system is maintaining two views of the data simultaneously — the WAL for streaming consumers, and the emerging Parquet files for analytics.",
      "Step 5 — Parquet files registered as an Iceberg snapshot. The StreamCatalog updates the linked lakehouse table. This is the atomic moment — the offset index and the Iceberg snapshot update in a single transaction. Databricks, Snowflake, Spark — anything pointing at that Iceberg table sees the new snapshot immediately. No connector ran. No ETL job woke up. The catalog just updated.",
      "Steps 1 through 3 are your streaming latency — milliseconds. Steps 4 and 5 are your analytics latency — seconds to minutes depending on your compaction config. That bounded lag is a deliberate design trade-off, not a limitation. Use the stream for operational queries where freshness is critical. Use the table for analytics where you want the full columnar query engine behind it.",
      "The thing I want you to hold onto is what didn't happen in those five steps. No connector cluster woke up. No schema registry sync ran. No staging bucket filled up and got processed by a separate job. One event, one write path, one catalog update. That's the architecture.",
    ],
    render: Slide8,
  },
  {
    id: 9, theme: 'beige',
    section: 'Pattern · Catalog Architecture',
    notes: [
      "You've seen how stream storage eliminates the duplicate data problem — one physical copy, two interfaces. This diagram shows how the same principle applies one layer up, to the catalog itself.",
      "The fragmentation problem we described for data has an exact parallel in metadata. Today you have a schema registry over here, an Iceberg catalog over there, and nothing that speaks both languages natively. The Stream Catalog is the bridge.",
      "Look at the three layers. At the top, the Protocol Layer — Kafka, Pulsar, REST/gRPC, MQTT — all stateless protocol servers. They don't hold config, they don't hold schema, they don't hold routing logic. Everything resolves through the catalog. That's the architectural shift: the protocol gateway holds zero state.",
      "On the bottom left, the Stream Catalog. This is where all access resolves — streams, schemas, volumes, data links, schema links. Notice it's pluggable. Your existing Iceberg catalog, your existing schema registry — they don't get ripped out. The Stream Catalog federates them. It becomes the unified metadata layer that both the streaming world and the lakehouse world can talk to.",
      "On the bottom right, Stream Storage — the layer we just covered. WAL for low-latency writes via BookKeeper, cost-optimized object storage for everything else, compacted down into Lakehouse Storage as Iceberg or Delta tables. The metadata arrow between the catalog and storage is the key: offset tracking, snapshot linkage, schema enforcement — all coordinated atomically in one place.",
      "The headline is this: just as stream storage gives you one physical copy of data accessible as both a stream and a table, the Stream Catalog gives you one metadata layer that bridges the schema registry and the Iceberg catalog. No sync job. No drift. The same governed contract, whether your consumer is a Kafka client or a Spark SQL query.",
    ],
    render: Slide9,
  },
  {
    id: 10, theme: 'beige',
    section: 'Pattern · Stream-Table Linkage',
    notes: [
      "The hierarchy is: StreamCatalog → Namespace → Stream, where each Stream has a 1:1 explicit linkage to a Lakehouse Table.",
      "That double-headed arrow — the 1:1 constraint — is deliberate, and I want to defend it proactively because someone will ask about fan-out.",
      "One stream, one table. Fan-out happens at the query layer: views, projections, materializations. Keeping storage 1:1 is what makes governance tractable. You cannot have a lineage story if one event can fork to arbitrary destinations at the storage layer.",
      "The key phrase to take away: One dataset, two faces. Stream for producers. Table for analysts. Governed by one catalog.",
    ],
    render: Slide10,
  },
  {
    id: 11, theme: 'beige',
    section: 'Pattern · Stream Metadata',
    notes: [
      "This is the operational win.",
      "Protocol gateways become stateless. They look up everything — schema, partition strategy, retention, routing — from the catalog at startup.",
      "No more connector workers with configuration only they know about. No more 2am incidents because a worker restarted and lost its schema mapping.",
      "The offset-to-snapshot mapping is what enables exactly-once delivery at the catalog level. You know which offsets have materialized to which Iceberg files. That's observable, auditable, and governed — not tribal knowledge.",
      "This should resonate immediately with anyone who has operated Kafka Connect at scale.",
    ],
    render: Slide11,
  },
  {
    id: 12, theme: 'beige',
    section: 'Pattern · Interoperability',
    notes: [
      "You don't have to rip anything out. The pattern federates with your existing stack — it doesn't replace it.",
      "That's the architecture. Now let's get concrete — because the pattern is abstract until you see what it looks like when the data is coming from millions of connected vehicles.",
    ],
    render: Slide12,
  },
  {
    id: 13, theme: 'beige',
    section: 'Part 02 · Real World Scale',
    notes: [
      "Everything you just heard is the pattern. The architecture.",
      "Now let's talk about why it matters when the data is coming from millions of connected vehicles.",
    ],
    render: Slide13,
  },
  {
    id: 14, theme: 'beige',
    section: 'Real World · Data Reality of a Modern Vehicle Fleet',
    notes: [
      "Let me set the scale.",
      "Millions of connected vehicles. Each one is a real-time telemetry stream. CAN bus, GPS, battery management, ADAS sensors — thousands of events per vehicle per second at peak load. Telemetry streams, OTA update events, manufacturing quality signals — all requiring unified, governed access.",
      "What makes this uniquely hard isn't the volume. It's three things:",
      "Safety-critical. A missed recall signal has real-world consequences. Schema changes require governance — not a Jira ticket.",
      "Regulatory. GDPR, ISO 26262, UNECE WP.29. Audit trails must trace every event from origin to insight. You cannot have gaps.",
      "Multi-protocol. Test benches don't speak Kafka. Cloud backends do. Both have to feed the same governed data lake.",
    ],
    render: Slide14,
  },
  {
    id: 15, theme: 'beige',
    section: 'Real World · Mapping the Architecture',
    notes: [
      "This is aspirational — let me be clear about that. This is the architecture we're working toward.",
      "Every Kafka topic maps to one Stream catalog entry, linked to one Iceberg table. No exceptions. The constraint is what makes the pattern enforceable at fleet scale.",
      "We'd keep our existing schema registry — teams already trust it. The catalog federates schema IDs. We meet teams where they are, not force a rip-and-replace.",
      "Consumer state — currently tracked in spreadsheets — becomes catalog queries. Any team can ask \"who is consuming vehicle-telemetry at what offset?\" without filing a ticket.",
      "Retention policies, today scattered across broker configs, get declared once in the catalog and enforced uniformly across all ingestion paths.",
      "The materialization decision for us is clear: Materialized mode for current vehicle state — VIN to latest state. Append mode for event replay and recall analysis. Two views. One stream. Zero ETL.",
      "Today that requires three separate pipelines and three separate copies of the data. The pattern collapses it to one stream, one catalog entry, one governed path.",
    ],
    render: Slide15,
  },
  {
    id: 16, theme: 'beige',
    section: 'Real World · Same Data · Three Consumers',
    notes: [
      "We've walked the pattern. Now let's see what it looks like when those broken pipelines are carrying data that matters.",
      "Two beats.",
      "A broken ETL is a missed recall signal. A firmware update adds a sensor field. The schema registry updates. The connector doesn't. Rows drop silently. In automotive, that's not a dashboard problem. That's a safety problem.",
      "Stale analytics in a safety-critical domain. A fleet-wide anomaly is emerging right now. Your BI team is looking at yesterday's data. Governance spanning four systems means no single team owns the gap — so nobody closes it.",
      "That's why this matters.",
    ],
    render: Slide16,
  },
  {
    id: 17, theme: 'beige',
    section: 'Call for Conversation',
    notes: [
      "We want to make this a conversation, not a presentation.",
      "Four concrete questions for this community:",
      "One — We've implemented this catalog interface. Does it feel right? What's missing from the spec?",
      "Two — How should streaming metadata sit alongside table metadata in the Iceberg spec? Is it additive, or does it require a structural change?",
      "Three — How should stream-to-table linkage handle fan-out and fan-in at the specification level?",
      "Four — Should stream-table linkage be first-class in the Iceberg catalog spec, or a separate extension?",
      "The paper is published. The implementation is running. The spec is yours. Let's figure out where they meet — find us after the session.",
    ],
    render: Slide17,
  },
  {
    id: 18, theme: 'beige',
    section: 'Close · Q&A',
    notes: [
      "Thank you. We're happy to take questions now, and we'll both be around after the session if you want to dig into the implementation or the automotive use case in more detail.",
    ],
    render: Slide18,
  },
  {
    id: 19, theme: 'beige',
    section: 'Demo 01 · Ursa · Stream = Table',
    notes: [
      "Demo 1 runs in cloud mode: kafka-demo.sh connects to Ursa cluster via SASL/SSL. Show kafka-produce.sh running in one terminal, DuckDB shell querying in another. Emphasize: no iceberg_bridge.py, no connector process — Ursa writes WAL bytes directly to S3 and compaction produces Iceberg files from the same bytes. The stream and table are physically the same data.",
      "URSA ENGINE — THE NATIVE APPROACH: With Ursa-powered clusters, data is written directly to object storage in open table formats (Iceberg, Delta Lake) as part of the storage layer itself — no separate connector needed. Ursa integrates with Iceberg REST Catalog, Unity Catalog, and Snowflake Open Catalog, meaning streaming data is instantly discoverable and queryable by engines like Databricks, Athena, or Spark without an ETL step. This architecture won the VLDB 2025 Best Industry Paper award.",
    ],
    render: Slide19,
  },
  {
    id: 20, theme: 'beige',
    section: 'Demo 02 · Protocol Expansion',
    notes: [
      "Demo 2 runs in local mode (Docker Compose). Two producers simultaneously: kafka-produce.sh and pulsar-produce.sh. KSN bridges the Pulsar topic to Kafka transparently. iceberg_bridge.py (Kafka consumer) writes both to Iceberg. DuckDB shows Bavaria sensors (001-010) from Kafka and Rhineland sensors (101-110) from Pulsar in the same table. Honest note: this uses a connector bridge — two physical copies. That's the transitional path; Ursa (Demo 1) is where we're going.",
      "LAKEHOUSE SINK CONNECTOR — THE TRADITIONAL APPROACH: This is an open-source Pulsar IO connector that consumes from Pulsar topics and writes to Lakehouse tables. Key details: Supported table formats: Delta Lake, Apache Iceberg, and Apache Hudi. Supported cloud storage: AWS S3, GCS, and Azure (ADLS Gen2). Output formats: Parquet (default) and Avro. Processing guarantee: Effectively-once (at-least-once with deduplication — not strict exactly-once). Batching controls: configurable max flush interval (default 120s) and max records per batch (default 10M). Schema: reads schema from the Pulsar topic's schema registry and maps it to the target table format. Bidirectional: there's also a Lakehouse Source connector that reads a Lakehouse table's changelog back into a Pulsar topic. This is the open-source community path — it works today for Pulsar, MQTT, and HTTP. Ursa's native WAL approach is where this is heading.",
    ],
    render: Slide20,
  },
] as const;
